import { setSelected, setSelectedRedactedAsset } from 'client/store/mapSlice/mapSlice';
import { useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import OlSelect from 'ol/interaction/Select';
import { MapContext } from 'os-elements';
import { singleClick } from 'ol/events/condition';

import Button from 'client/components/aria/Button/Button';
import { mapMenuTabKeys, mapModes } from 'client/utils/constants/appStates';

import * as mapConfigParser from './mapConfigLookup';
import * as styleRuleEvaluator from 'client/utils/evaluators/styleRuleEvaluator';

import styles from './SelectFeature.module.scss';
import SelectFeaturePanel from './SelectFeaturePanel/SelectFeaturePanel';
import RedactedAssetPanel from './RedactedAssetPanel/RedactedAssetPanel';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import { selectSectorSpecificStyles } from 'client/store/layersSlice/layersSlice';
import useEscapeKeypress from 'client/utils/helpers/KeyHelpers/useEscapeKeypress';

import { styleRuleToIcon } from 'client/pages/MapPage/components/Legend/Legend.utils';
import LegendItem from 'client/pages/MapPage/components/Legend/LegendItem/LegendItem';
import { useActiveStep } from './useActiveStep';

const { ENHANCED_MEASURES } = mapMenuTabKeys;

const SelectFeature = ({ assets, mapConfig, layerStyles, mapMode }) => {
    const intl = useIntl();
    const { map } = useContext(MapContext);
    const ref = useRef(assets);
    const [open, setOpen] = useState(false);
    const selected = useSelector(state => state?.map?.selected);
    const { activeStep, prev, next, resetStep } = useActiveStep(selected?.length || 0);
    const selectedRedactedAsset = useSelector(state => state?.map?.selectedRedactedAsset);
    const selectedKey = useSelector(state => state?.map?.menuSelectedTab);
    const sectorSpecificStyles = useSelector(selectSectorSpecificStyles);
    const dispatch = useDispatch();
    const [featureDetails, setFeatureDetails] = useState(null);
    const currentLocale = useSelector(state => state?.locale?.current);

    useEffect(() => {
        if (!selected || selected.length === 0) {
            setFeatureDetails(null);
            return;
        }

        const feature = selected[activeStep - 1];
        if (!feature) {
            setFeatureDetails(null);
            return;
        }

        const featureId = feature.getId();
        const assetType = featureId.split('.')[0];
        const layerConfig = mapConfig?.result?.layers?.find(layer => layer?.assetType === assetType);
        const layerName = layerConfig?.displayName[currentLocale];
        const styleRule = styleRuleEvaluator.evaluateStyleRules(feature, layerConfig?.styleRules, sectorSpecificStyles);
        const styleId = styleRule?.selectedStyleId || styleRule?.styleId;
        const style = mapConfig?.result?.styles?.find(style => style.id === styleId);
        const icon = style ? styleRuleToIcon(style) : null;

        setFeatureDetails({
            layerName,
            icon
        });
    }, [selected, activeStep, mapConfig, sectorSpecificStyles, currentLocale]);

    useEffect(() => {
        setOpen(selected?.length > 0 || selectedRedactedAsset !== null);
        resetStep();
    }, [selected, selectedRedactedAsset]);

    useEffect(() => {
        ref.current = Object.keys(assets);
    }, [assets]);

    useEffect(() => {
        let select;
        if (selectedKey !== ENHANCED_MEASURES && mapMode.includes(mapModes.AOI)) {
            if (!select) {
                select = new OlSelect({
                    layers: handleSelectable,
                    condition: singleClick,
                    multi: true,
                    hitTolerance: 10,
                    style: (feature) => styleFunction(feature, mapConfig)
                });
                select.on('select', (evt) => handleSelect(evt, select));
            }
            map?.addInteraction(select);
        }
        return () => {
            // gets triggered whenever dependency changes
            map?.removeInteraction(select);
        };
    }, [mapMode, selectedKey, mapConfig?.result, sectorSpecificStyles]);

    const handleSelect = (evt, select) => {
        const selected = evt?.selected || [];
        selected.sort((a, b) => select.getLayer(b).getZIndex() - select.getLayer(a).getZIndex());
        dispatch(setSelected(selected));
    };

    const handleSelectable = (layer) => {
        return ref.current.includes(layer.get('id'));
    };

    const handleClose = () => {
        setOpen(false);
        dispatch(setSelected([]));
        dispatch(setSelectedRedactedAsset(null));
        map.getInteractions().getArray().forEach(interaction => {
            if (interaction instanceof OlSelect) {
                interaction?.getFeatures().clear();
            }
        });
    };
    useEscapeKeypress(handleClose);

    const styleFunction = (feature, mapConfig) => {
        const layer = mapConfigParser.extractMapConfigForLayer(mapConfig.result, feature.getId());
        const styleRuleToApply = styleRuleEvaluator.evaluateStyleRules(feature, layer.styleRules, sectorSpecificStyles);
        const styleBasedOnSelectionStatus = layerStyles[styleRuleToApply?.selectedStyleId]
            ? layerStyles[styleRuleToApply?.selectedStyleId]
            : layerStyles[styleRuleToApply?.styleId];
        if (typeof styleBasedOnSelectionStatus === 'function') {
            return styleBasedOnSelectionStatus(feature);
        }
        return styleRuleEvaluator.evaluateRotation(styleRuleToApply, feature, styleBasedOnSelectionStatus);
    };

    if (!selected && !selectedRedactedAsset) {
        return;
    }

    const pageButtonEnabled = selected.length > 1;
    const isRedactedAsset = selectedRedactedAsset !== null;

    return (
        <div data-testid="selectFeature.panel" className={['govuk-main-wrapper', styles.panel, (open ? styles.open : null)].join(' ')}>
            <div className={styles.header}>
                {isRedactedAsset && (

                    <LegendItem prefix={<div className={styles.redactedIconContainer}><span className={styles.redactedIcon} /></div>} className={styles.legend}>
                        <div data-testid="selectFeature.panel.header" className={['govuk-!-font-weight-bold govuk-heading-s', styles.headerText].join(' ')}>
                            <FormattedMessage id="selectFeature.redactedAssetHeader" />
                        </div>
                    </LegendItem>
                )}
                {!isRedactedAsset && featureDetails &&
                    <LegendItem prefix={featureDetails.icon} className={styles.legend}>
                        <div data-testid="selectFeature.panel.header" className={['govuk-!-font-weight-bold govuk-heading-s', styles.headerText].join(' ')}>
                            {featureDetails.layerName}
                        </div>
                    </LegendItem>
                }
                <div className={styles.close} aria-hidden={!open}>
                    <Button className={styles.closeButton} data-testid="selectFeature.panel.close" variant={'secondary'} onPress={handleClose} aria-label={intl.formatMessage({ id: 'selectFeature.button.close' })}>
                        <Icon path={mdiClose} size={5}/>
                    </Button>
                </div>
            </div>

            {!isRedactedAsset && (
                <div className={styles.nav} aria-hidden={!open}>
                    <Button data-testid="selectFeature.button.previous" onPress={prev} isDisabled={!pageButtonEnabled}>
                        <FormattedMessage id="selectFeature.button.previous"/>
                    </Button>
                    <div className={styles.navLabel}>
                        <FormattedMessage
                            id="selectFeature.stepXofY"
                            values={{
                                x: activeStep,
                                y: selected.length
                            }}>
                        </FormattedMessage>
                    </div>
                    <Button data-testid="selectFeature.button.next" onPress={next} isDisabled={!pageButtonEnabled}>
                        <FormattedMessage id="selectFeature.button.next"/>
                    </Button>
                </div>
            )}
            {isRedactedAsset
                ? <RedactedAssetPanel assetData={selectedRedactedAsset} />
                : selected.length
                    ? <SelectFeaturePanel activeStep={activeStep} />
                    : null
            }
        </div>

    );
};

SelectFeature.propTypes = {
    map: PropTypes.func,
    mapMode: PropTypes.array,
    mapConfig: PropTypes.object,
    dataproviderassigneduniqueid: PropTypes.string,
    linkedFiles: PropTypes.array,
    guidances: PropTypes.array,
    nuarActor: PropTypes.object,
    assets: PropTypes.any,
    layerStyles: PropTypes.object,
    supplementaryData: PropTypes.any
};

const mapStateToProps = state => {
    const { map, assets, mapConfig, layers } = state;
    return {
        mapMode: map?.mode || [],
        mapConfig: mapConfig || {},
        assets: assets?.layers || [],
        layerStyles: layers?.result?.styles || {}
    };
};

export default connect(mapStateToProps)(SelectFeature);
